/**
 * Knock Doorway loader
 *
 * Single, framework-agnostic, self-healing loader for the Knock Doorway
 * widget. It exists because the Doorway bootstrap (doorway.min.js) is
 * fragile in two ways that broke our React sites:
 *
 *  1. Intermittent API failures. `inject()` XHRs the Doorway API for
 *     property data, and the V3 app then makes its own data fetch. Either
 *     call 500s at random — the bootstrap throws "Error pulling property
 *     data" (uncatchable, fires inside an XHR callback) and the V3 app logs
 *     "Failed to load application: Cannot read properties of undefined
 *     (reading 'public_id')". In both cases no launcher is ever mounted.
 *
 *  2. React remounts. The block lives in an injection slot that mounts on
 *     every page and remounts on SPA navigation / StrictMode. The old block
 *     removed and re-added the bootstrap script on every mount, tearing down
 *     a healthy widget and re-rolling the dice on the flaky API each time.
 *
 * This loader fixes both:
 *
 *  - It is a window-scoped singleton. Many mounts (StrictMode, navigation,
 *    multiple blocks) collapse to one managed load. Once the launcher is
 *    attached it stays attached — React unmount never tears it down.
 *  - It treats the presence of the launcher DOM (`#knockDoorwayRoot`, which
 *    the V3 app creates only on a fully successful load) as the success
 *    signal. If the launcher does not appear, it performs a full teardown
 *    (drop the bootstrap + app scripts + any partial root + the global) and
 *    reloads from scratch, with exponential backoff. This is version-
 *    agnostic: it does not depend on Knock's internal reload() behavior.
 *  - After exhausting retries it parks in `error` and revives on the next
 *    `online` / tab-visible event, so a prolonged outage recovers without
 *    hammering the API.
 */

const SCRIPT_URL = 'https://doorway.knck.io/latest/doorway.min.js';
const SCRIPT_ID = 'knock-doorway-script';
const ACTION_ATTR = 'data-knock-action';
const MANAGER_KEY = '__knockDoorwayLoader__';

/** The V3 app mounts its React root into this id; only created on success. */
const ROOT_ID = 'knockDoorwayRoot';
/** Theme-vars node the app appends; cleaned up on teardown to avoid leaks. */
const THEME_VARS_ID = 'doorway-theme-vars';
/**
 * Success = any of these are present. `#knockDoorwayRoot` is the primary
 * signal — the V3 app creates it synchronously when it mounts, and only after
 * the property + profile data loaded cleanly. The `[class*="doorway-launcher"]`
 * fallback covers the rendered launcher chrome (observed classes:
 * doorway-launcher-button-wrapper / -frame) and any V2 variant.
 */
const LAUNCHER_SELECTOR = `#${ROOT_ID}, [class*="doorway-launcher"]`;

// Timing. Tuned aggressively because tour CTAs depend on the launcher being
// present — we'd rather retry hard than leave a visitor unable to book.
//
//   - FAST_FAIL only waits on the tiny property XHR (isInjected flips true the
//     instant that 200s, before the heavy app bundle loads), so 3s is safe
//     even on slow connections and snappy when the API 500s.
//   - HEALTH_TIMEOUT covers the heavier "injected but app never rendered"
//     case; the bundle is cached after the first try, so retries are cheap.
//   - Backoff recovers fast from transient 500s (1s, 2s, 4s) then settles to a
//     steady 6s cadence instead of ballooning.
//   - MAX_ATTEMPTS x this backoff ≈ 2.5–3 min of foreground retrying before we
//     park in `error`; we then revive on the next online / tab-visible event,
//     so an actively-used tab keeps trying indefinitely in practice.
const POLL_MS = 250;
const BOOTSTRAP_TIMEOUT_MS = 8000;
const FAST_FAIL_MS = 3000;
const HEALTH_TIMEOUT_MS = 10000;
const MAX_ATTEMPTS = 20;
const BACKOFF_BASE_MS = 1000;
const BACKOFF_MAX_MS = 6000;

type DoorwayScope = 'community';

type KnockDoorway = {
  init: (token: string, propertyType: DoorwayScope, propertyId: string) => void;
  inject?: () => void;
  isInjected?: boolean;
  isInitialized?: boolean;
  openScheduling?: () => void;
  openMessaging?: () => void;
};

type LoaderStatus = 'idle' | 'loading' | 'ready' | 'error';

interface LoaderState {
  apiToken: string;
  communityId: string;
  status: LoaderStatus;
  attempts: number;
  /** Ticks elapsed in the current attempt's health check. */
  healthTicks: number;
  pollTimer: number | null;
  retryTimer: number | null;
  revivalBound: boolean;
}

interface WindowWithDoorway extends Window {
  knockDoorway?: KnockDoorway;
  [MANAGER_KEY]?: LoaderState;
}

function win(): WindowWithDoorway | null {
  return typeof window === 'undefined' ? null : (window as unknown as WindowWithDoorway);
}

function getDoorway(w: WindowWithDoorway): KnockDoorway | null {
  return w.knockDoorway ?? null;
}

function launcherPresent(): boolean {
  return !!document.querySelector(LAUNCHER_SELECTOR);
}

// ---------------------------------------------------------------------------
// Teardown
// ---------------------------------------------------------------------------

/**
 * Drop every artifact of a Doorway load so the next attempt starts from a
 * clean slate. Removes the bootstrap script, the app scripts it injected
 * (doorway.knck.io/v2|v3 main bundles), the React root + theme-vars nodes,
 * and the global object. Re-appending the bootstrap script afterwards
 * re-executes it and rebuilds `window.knockDoorway` from scratch.
 */
function tearDownWidget(w: WindowWithDoorway): void {
  document.getElementById(SCRIPT_ID)?.remove();
  document
    .querySelectorAll<HTMLScriptElement>('script[src*="doorway.knck.io"]')
    .forEach((el) => el.remove());
  document.getElementById(ROOT_ID)?.remove();
  document.getElementById(THEME_VARS_ID)?.remove();
  delete w.knockDoorway;
}

function clearTimers(state: LoaderState): void {
  if (state.pollTimer !== null) {
    clearTimeout(state.pollTimer);
    state.pollTimer = null;
  }
  if (state.retryTimer !== null) {
    clearTimeout(state.retryTimer);
    state.retryTimer = null;
  }
}

// ---------------------------------------------------------------------------
// Bootstrap script
// ---------------------------------------------------------------------------

/**
 * Ensure `window.knockDoorway` exists, loading the bootstrap script into
 * <head> if needed. Resolves once the global is available; rejects on
 * script error or timeout. Polling (rather than relying solely on the load
 * event) handles the case where a script element is already present but the
 * global has not finished evaluating yet.
 */
function ensureBootstrap(w: WindowWithDoorway): Promise<void> {
  if (getDoorway(w)) return Promise.resolve();

  return new Promise((resolve, reject) => {
    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = SCRIPT_URL;
      script.async = true;
      script.addEventListener('error', () =>
        reject(new Error('Knock Doorway bootstrap failed to load')),
      );
      document.head.appendChild(script);
    }

    let waited = 0;
    const tick = () => {
      if (getDoorway(w)) return resolve();
      waited += POLL_MS;
      if (waited >= BOOTSTRAP_TIMEOUT_MS) {
        reject(new Error('Knock Doorway bootstrap timed out'));
        return;
      }
      setTimeout(tick, POLL_MS);
    };
    setTimeout(tick, POLL_MS);
  });
}

// ---------------------------------------------------------------------------
// Attempt lifecycle
// ---------------------------------------------------------------------------

function scheduleRetry(w: WindowWithDoorway, state: LoaderState): void {
  if (state.attempts >= MAX_ATTEMPTS) {
    state.status = 'error';
    bindRevival(w, state);
    return;
  }
  const delay = Math.min(
    BACKOFF_BASE_MS * 2 ** (state.attempts - 1),
    BACKOFF_MAX_MS,
  );
  state.retryTimer = setTimeout(() => {
    state.retryTimer = null;
    runAttempt(w, state);
  }, delay) as unknown as number;
}

function onAttemptFailed(w: WindowWithDoorway, state: LoaderState): void {
  clearTimers(state);
  tearDownWidget(w);
  scheduleRetry(w, state);
}

function startHealthCheck(w: WindowWithDoorway, state: LoaderState): void {
  state.healthTicks = 0;
  const tick = () => {
    state.pollTimer = null;

    if (launcherPresent()) {
      state.status = 'ready';
      clearTimers(state);
      return;
    }

    state.healthTicks += 1;
    const elapsed = state.healthTicks * POLL_MS;
    const doorway = getDoorway(w);

    // Fast path: the property XHR 500'd, so the widget never began injecting.
    // No point waiting out the full timeout — tear down and retry now.
    if (elapsed >= FAST_FAIL_MS && doorway && doorway.isInjected !== true) {
      onAttemptFailed(w, state);
      return;
    }

    if (elapsed >= HEALTH_TIMEOUT_MS) {
      onAttemptFailed(w, state);
      return;
    }

    state.pollTimer = setTimeout(tick, POLL_MS) as unknown as number;
  };
  state.pollTimer = setTimeout(tick, POLL_MS) as unknown as number;
}

function runAttempt(w: WindowWithDoorway, state: LoaderState): void {
  state.attempts += 1;
  state.status = 'loading';

  ensureBootstrap(w)
    .then(() => {
      const doorway = getDoorway(w);
      if (!doorway) {
        onAttemptFailed(w, state);
        return;
      }
      doorway.init(state.apiToken, 'community', state.communityId);
      if (!doorway.isInjected) doorway.inject?.();
      startHealthCheck(w, state);
    })
    .catch(() => onAttemptFailed(w, state));
}

// ---------------------------------------------------------------------------
// Revival after giving up
// ---------------------------------------------------------------------------

/**
 * After MAX_ATTEMPTS we stop polling, but a chatbot should still appear once
 * connectivity returns. Re-kick a single attempt when the tab regains focus
 * or the browser comes back online, resetting the backoff counter.
 */
function bindRevival(w: WindowWithDoorway, state: LoaderState): void {
  if (state.revivalBound) return;
  state.revivalBound = true;

  const revive = () => {
    const current = w[MANAGER_KEY];
    if (!current || current !== state) return;
    if (current.status !== 'error') return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    current.attempts = 0;
    runAttempt(w, current);
  };

  window.addEventListener('online', revive);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') revive();
  });
}

// ---------------------------------------------------------------------------
// data-knock-action delegate (forward-compatible no-op today)
// ---------------------------------------------------------------------------

let actionDelegateBound = false;

/**
 * Theme-authored CTAs tagged `data-knock-action="schedule|message"` route to
 * the Doorway open* methods when the library exposes them. The current V3
 * library does not, so this silently no-ops (never preventDefault unless we
 * can actually handle the click, so the host element keeps its native
 * behavior). Bound once, globally — survives block remounts.
 */
function bindActionDelegate(w: WindowWithDoorway): void {
  if (actionDelegateBound) return;
  actionDelegateBound = true;

  document.addEventListener('click', (event: MouseEvent) => {
    const target = event.target as Element | null;
    const trigger = target?.closest(`[${ACTION_ATTR}]`);
    if (!trigger) return;
    const doorway = getDoorway(w);
    if (!doorway) return;

    const action = trigger.getAttribute(ACTION_ATTR);
    const method =
      action === 'schedule'
        ? doorway.openScheduling
        : action === 'message'
          ? doorway.openMessaging
          : null;

    if (typeof method === 'function') {
      event.preventDefault();
      method.call(doorway);
    }
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Idempotently ensure the Knock Doorway widget is loaded for the given
 * credentials. Safe to call on every block mount: repeat calls with the same
 * credentials are no-ops while a load is in flight or already succeeded;
 * changed credentials trigger a clean reload.
 */
export function ensureKnockDoorway(apiToken: string, communityId: string): void {
  const w = win();
  if (!w || !apiToken || !communityId) return;

  bindActionDelegate(w);

  const existing = w[MANAGER_KEY];
  if (existing) {
    const sameConfig =
      existing.apiToken === apiToken && existing.communityId === communityId;
    if (sameConfig) {
      // A healthy or in-flight load owns this config — leave it alone.
      // Only re-kick if we previously parked in `error`.
      if (existing.status === 'error') {
        existing.attempts = 0;
        runAttempt(w, existing);
      }
      return;
    }
    // Credentials changed: reset everything and reload from scratch.
    clearTimers(existing);
    tearDownWidget(w);
  }

  const state: LoaderState = {
    apiToken,
    communityId,
    status: 'idle',
    attempts: 0,
    healthTicks: 0,
    pollTimer: null,
    retryTimer: null,
    revivalBound: false,
  };
  w[MANAGER_KEY] = state;
  runAttempt(w, state);
}

/**
 * Full reset — used by tests and available for hard re-initialization. Clears
 * timers, removes all widget DOM/scripts, and drops the singleton.
 */
export function resetKnockDoorway(): void {
  const w = win();
  if (!w) return;
  const state = w[MANAGER_KEY];
  if (state) clearTimers(state);
  tearDownWidget(w);
  delete w[MANAGER_KEY];
}

/** Current loader status, for diagnostics/tests. */
export function getKnockDoorwayStatus(): LoaderStatus {
  return win()?.[MANAGER_KEY]?.status ?? 'idle';
}

export const __testing = {
  SCRIPT_ID,
  ROOT_ID,
  POLL_MS,
  FAST_FAIL_MS,
  HEALTH_TIMEOUT_MS,
  BOOTSTRAP_TIMEOUT_MS,
  MAX_ATTEMPTS,
  BACKOFF_BASE_MS,
  BACKOFF_MAX_MS,
};
